import {Trans} from '@lingui/macro';
import React, {FC, memo, useState} from 'react';

import {Handler, HandlerOf, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {SignatureModel} from '../../../../models/api/signatureModel';
import {DropdownItemSecondaryContent} from '../../../common/dropdowns/dropdownItemSecondaryContent';
import {ComposerSectionButton} from '../common/composerSectionButton';
import {ComposerSectionEditorCoordinator} from '../common/composerSectionEditorCoordinator';
import {EmailComposerStatic} from '../common/emailComposerStatic';

/*
 * Props.
 */

export interface SignaturesProps {
  message: DraftMessageModel;
  availableSignatures: ReadonlyArray<SignatureModel>;
  /** Interpolated signature HTML. */
  signatureHtml?: string;
  isAlwaysEditing: boolean;
  editSignature: Handler;
  revertSignature: Handler;
  removeSignature: Handler;
  addSignature: Handler;
  replaceSignature: HandlerOf<number>;
  renderEditor: Renderer;
  hasFocus?: boolean;
}

interface SignaturesReplaceButtonProps {
  availableSignatures: ReadonlyArray<SignatureModel>;
  selectedSignatureId: number | undefined;
  replaceSignature: HandlerOf<number>;
  onDropdownToggle?: HandlerOf<boolean>;
}

/*
 * Component.
 */

export const Signatures: FC<SignaturesProps> = memo((props) => {
  const [isReplaceDropdownOpen, setIsReplaceDropdownOpen] = useState(false);

  const {message, availableSignatures, isAlwaysEditing, renderEditor, hasFocus} = props;
  if (availableSignatures.length === 0) {
    return null;
  }

  return (
    <ComposerSectionEditorCoordinator
      isIncluded={Boolean(message.isSignatureIncluded)}
      onAdd={props.addSignature}
      onRemove={props.removeSignature}
      renderAddLabel={() => <Trans id="composer-signature-add">Add signature</Trans>}
      renderRemoveLabel={() => <Trans id="composer-signature-remove">Remove signature</Trans>}
      renderStatic={() => <EmailComposerStatic html={props.signatureHtml} />}
      isAlwaysEditing={isAlwaysEditing}
      isEditing={Boolean(message.wasSignatureModified)}
      onEdit={props.editSignature}
      onRevert={props.revertSignature}
      renderEditor={renderEditor}
      hasHover={isReplaceDropdownOpen}
      hasFocus={hasFocus}
      renderExtraRightActions={() => (
        <SignaturesReplaceButton
          availableSignatures={availableSignatures}
          selectedSignatureId={message.signatureId}
          replaceSignature={props.replaceSignature}
          onDropdownToggle={setIsReplaceDropdownOpen}
        />
      )}
    />
  );
});

const SignaturesReplaceButton: FC<SignaturesReplaceButtonProps> = (props) => {
  // If there's only signature, it's already being displayed.
  if (props.availableSignatures.length === 1) {
    return null;
  }

  // Otherwise, render the button.
  return (
    <PopoverCoordinator
      onPopoverToggle={props.onDropdownToggle}
      renderButton={renderReplaceButton}
      renderModal={(modalProps) => renderReplaceDropdown(modalProps, props)}
    />
  );
};

function renderReplaceButton(isOpen: boolean) {
  return (
    <div>
      <TooltipCoordinator render={() => <Trans id="composer-signature-replace">Switch signature</Trans>}>
        <ComposerSectionButton hasHover={isOpen}>
          <Icon name="arrowsTiny" />
          <VisuallyHidden>
            <Trans id="composer-signature-replace">Switch signature</Trans>
          </VisuallyHidden>
        </ComposerSectionButton>
      </TooltipCoordinator>
    </div>
  );
}

function renderReplaceDropdown(modalProps: PopoverTransferProps, props: SignaturesReplaceButtonProps) {
  const {selectedSignatureId, availableSignatures, replaceSignature} = props;

  // Render the items.
  const dropdownItems = availableSignatures.map((s) => {
    const isChecked = selectedSignatureId === s.id;
    const title = s.name || s.senderInfo;

    return (
      <DropdownItem key={s.id} onSelect={() => replaceSignature(s.id)} isChecked={isChecked}>
        <DropdownItemContent isChecked={isChecked}>{title}</DropdownItemContent>
        <DropdownItemSecondaryContent>{s.blurb}</DropdownItemSecondaryContent>
      </DropdownItem>
    );
  });

  // Render the dropdown.
  return (
    <SimpleDropdown {...modalProps} placement="bottom-end" width={250}>
      {dropdownItems}
    </SimpleDropdown>
  );
}
